import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDoctorById, getSlots, bookAppointment, getDepartments } from '../services/doctorsApi';
import { payWithRazorpay } from '../services/razorpayService';
import { createPatient, patientToBookingForm } from '../services/patientApi';
import PatientAutofill from '../components/PatientAutofill';
import { formatDateLocal } from '../utils/dateUtils';
import { useTenant } from '../contexts/TenantContext';

const PLATFORM_FEE = 20;
const TAX_RATE = 0.18;

const buildDateOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    // Use local timezone to format date (not UTC) to avoid timezone conversion issues
    const iso = formatDateLocal(d);
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow'
      : d.toLocaleDateString(undefined, { weekday: 'short' });
    const dayNum = d.getDate();
    const monthShort = d.toLocaleDateString(undefined, { month: 'short' });
    out.push({ iso, label, day: dayNum, month: monthShort });
  }
  return out;
};

const BookAppointment = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { doctorId } = useParams();
  const [searchParams] = useSearchParams();
  const mode       = searchParams.get('mode') === 'walk-in' ? 'walk-in' : 'online';
  const department = searchParams.get('department') || '';
  const preSecondOpinion = searchParams.get('secondOpinion') === 'true';
  const hospitalId = searchParams.get('hospitalId'); // Get hospitalId from query params

  // Online tele flow has no doctorId in the URL
  const isAutoAssign = mode === 'online' && !doctorId;
  // Hospital flow: walk-in with hospitalId but no doctorId
  const isHospitalFlow = mode === 'walk-in' && hospitalId && !doctorId;

  const { user } = useAuth();

  const [doctor,   setDoctor]   = useState(null);
  const [loading,  setLoading]  = useState(!isAutoAssign && !isHospitalFlow);
  const [error,    setError]    = useState(null);

  // Hospital-specific state
  const [hospital, setHospital] = useState(null);
  const [hospitalDoctors, setHospitalDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');

  const dateOptions   = useMemo(() => buildDateOptions(), []);
  const [selectedDate, setSelectedDate] = useState(dateOptions[0].iso);

  const [slots,        setSlots]        = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [patient, setPatient] = useState({
    name:                  user?.name  || '',
    phone:                 user?.phone || '',
    email:                 user?.email || '',
    age:                   '',
    gender:                '',
    currentComplaint:      '',
    previousHealthHistory: '',
    relation:              'SELF',
  });

  const [submitting,      setSubmitting]      = useState(false);
  const [confirmation,    setConfirmation]    = useState(null);
  const [formError,       setFormError]       = useState('');
  const [isSecondOpinion, setIsSecondOpinion] = useState(preSecondOpinion);
  const [departments,     setDepartments]     = useState([]);
  const [selectedDept,    setSelectedDept]    = useState(department || '');
  const [reportFile,      setReportFile]      = useState(null);

  // Load hospital data if hospitalId is provided
  useEffect(() => {
    if (!isHospitalFlow || !hospitalId) return;
    setLoading(true);
    setError(null);
    import('../services/api').then(({ default: api }) => {
      api.get(`/consultations/hospitals?hospitalId=${hospitalId}`)
        .then(({ data }) => {
          if (data && data.length > 0) {
            const hospitalData = data[0];
            setHospital(hospitalData);
            setHospitalDoctors(hospitalData.doctors || []);
          } else {
            setError('Hospital not found.');
          }
        })
        .catch(() => setError('Could not load hospital details.'))
        .finally(() => setLoading(false));
    });
  }, [hospitalId, isHospitalFlow]);

  // Load doctor for direct doctor booking (from doctor list page)
  useEffect(() => {
    if (isAutoAssign || isHospitalFlow || !doctorId) return;
    setLoading(true);
    getDoctorById(doctorId, mode)
      .then((d) => { setDoctor(d); setLoading(false); })
      .catch(() => { setError('Could not load doctor.'); setLoading(false); });
  }, [doctorId, isAutoAssign, isHospitalFlow, mode]);

  // When user selects a doctor from hospital dropdown, load that doctor's details
  useEffect(() => {
    if (!isHospitalFlow || !selectedDoctorId) {
      setDoctor(null);
      return;
    }
    const selectedDoctor = hospitalDoctors.find(d => d.id === selectedDoctorId);
    if (selectedDoctor) {
      setDoctor(selectedDoctor);
    }
  }, [selectedDoctorId, hospitalDoctors, isHospitalFlow]);

  // Load departments list for the dropdown
  useEffect(() => {
    getDepartments().then(depts => setDepartments(depts.filter(d => d.id !== 'all'))).catch(() => {});
  }, []);

  // Load slots only for walk-in
  useEffect(() => {
    // Determine the effective doctor ID (from URL param or hospital selection)
    const effectiveDoctorId = isHospitalFlow ? selectedDoctorId : doctorId;

    if (isAutoAssign || !effectiveDoctorId || !selectedDate) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    getSlots(effectiveDoctorId, selectedDate, mode)
      .then((s) => { setSlots(s); setSlotsLoading(false); })
      .catch(() => setSlotsLoading(false));
  }, [doctorId, selectedDoctorId, selectedDate, isAutoAssign, isHospitalFlow, mode]);

  const fee   = doctor?.fee ?? 0;
  const tax   = Math.round(fee * TAX_RATE);
  const total = fee + tax + PLATFORM_FEE;

  const validate = () => {
    if (!isAutoAssign && !selectedSlot) return 'Pick a time slot to continue.';
    if (!patient.name.trim()) return 'Patient name is required.';
    if (!patient.phone.match(/^[6-9]\d{9}$/)) return 'Enter a valid 10-digit mobile number.';
    if (!patient.age || isNaN(patient.age) || patient.age < 0 || patient.age > 120) return 'Enter a valid age.';
    if (!patient.gender) return 'Select gender.';
    if (isAutoAssign && !selectedDept) return 'Please select a department / specialty.';
    return null;
  };

  // Derived: mirrors validate() rules reactively — enables the button live
  const isFormReady =
    (isAutoAssign || !!selectedSlot) &&
    patient.name.trim() !== '' &&
    /^[6-9]\d{9}$/.test(patient.phone) &&
    !!patient.age && !isNaN(patient.age) && Number(patient.age) >= 0 && Number(patient.age) <= 120 &&
    patient.gender !== '' &&
    (!isAutoAssign || !!selectedDept);

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setFormError(v); return; }
    setFormError('');
    setSubmitting(true);
    try {
      // ── NEW: pay via Razorpay first when there's a fee to collect ─────────
      // For auto-assign online bookings the doctor (and hence fee) is only
      // resolved server-side, so payment there is still handled by the
      // backend allowing a free booking when total is 0 — this covers the
      // common "book with a known doctor/fee" path.
      let payment = null;
      if (total > 0) {
        try {
          payment = await payWithRazorpay({
            amount: total,
            purpose: 'APPOINTMENT',
            description: `Consultation with ${doctor?.name || 'doctor'}`,
            tenantName: tenant?.name,
            prefill: {
              name: patient.name,
              contact: patient.phone,
              email: patient.email || undefined,
            },
          });
        } catch (payErr) {
          setFormError(payErr.message || 'Payment was not completed.');
          setSubmitting(false);
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────

      const effectiveDoctorId = isHospitalFlow ? selectedDoctorId : doctorId;
      const res = await bookAppointment({

        doctorId:   isAutoAssign ? null : effectiveDoctorId,
        slotId:     isAutoAssign ? null : selectedSlot.id,
        department: isAutoAssign ? (selectedDept || department) : selectedDept || undefined,
        appointmentDate: selectedDate,
        mode,
        patient,
        isSecondOpinion,
        reportFile: reportFile || undefined,
        payment,
      });
      setConfirmation(res);

      // ── Save patient profile ────────────────────────────────────
      // Whether this is the user's own record is driven by the "Relationship
      // to Patient" field they picked, not just by whether autofill data changed —
      // previously this was ignored entirely, so a family-member booking with
      // the same contact phone as the account holder would silently overwrite
      // the user's own self profile.
      try {
        const isSelf = patient.relation === 'SELF';

        await createPatient({
          fullName: patient.name.trim(),
          phone:    patient.phone || null,
          email:    patient.email || null,
          age:      parseInt(patient.age, 10) || null,
          gender:   patient.gender || null,
          isSelf,
          relation: isSelf ? 'SELF' : (patient.relation || 'OTHER'),
        });
      } catch {
        // Best-effort — never block the booking confirmation
      }
    } catch {
      setFormError('Could not confirm appointment. Try again.');
    }
    setSubmitting(false);
  };

  // Autofill: fill the patient form from a saved patient profile
  const handleAutofill = (savedPatient) => {
    setPatient(prev => ({
      ...patientToBookingForm(savedPatient),
      currentComplaint:      prev.currentComplaint,
      previousHealthHistory: prev.previousHealthHistory,
    }));
  };

  /* ── early states ─────────────────────────────────────────────── */
  if (loading) {
    return <div className="page"><main className="page-main"><div className="t-body-sm">Loading…</div></main></div>;
  }
  if (error) {
    return (
      <div className="page">
        <main className="page-main">
          <div className="error-state"><span>⚠️</span> {error}</div>
          <button className="btn-primary" style={{ maxWidth: 280, marginTop: 16 }}
            onClick={() => navigate('/services/online-consultation')}>
            Back to doctors
          </button>
        </main>
      </div>
    );
  }

  /* ── confirmation screen ──────────────────────────────────────── */
  if (confirmation) {
    const dateLabel = dateOptions.find((d) => d.iso === selectedDate);
    return (
      <div className="page">
        <main className="page-main">
          <div className="card-elevated" style={{ maxWidth: 560, margin: '40px auto', textAlign: 'center', animation: 'successIn .5s ease both' }}>
            <div style={{
              width: 64, height: 64, margin: '0 auto 16px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" width="30" height="30">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h1 className="t-h1" style={{ marginBottom: 6 }}>Appointment confirmed</h1>
            <p className="t-body-sm" style={{ marginBottom: 20 }}>
              Confirmation ID: <strong>{confirmation.confirmationId}</strong>
            </p>
            <div style={{
              background: '#f9fafb', borderRadius: 12, padding: 16,
              textAlign: 'left', marginBottom: 20,
            }}>
              <div style={{ marginBottom: 8 }}>
                <strong>{confirmation.doctorName}</strong> — {confirmation.doctorClinic || 'Online'}
              </div>
              <div className="t-body-sm">
                {dateLabel?.label}, {dateLabel?.day} {dateLabel?.month}
                {confirmation.slotTime ? ` · ${confirmation.slotTime}` : ''}
              </div>
              <div className="t-body-sm">
                {isSecondOpinion ? '🔍 Second Opinion' : '📱 Online consultation'}
              </div>
              {confirmation.total && (
                <div className="t-body-sm" style={{ marginTop: 6 }}>Total paid: ₹{confirmation.total}</div>
              )}
            </div>
            <button className="btn-primary" onClick={() => navigate('/')}>Back to home</button>
          </div>
        </main>
      </div>
    );
  }

  /* ── main booking screen ─────────────────────────────────────── */
  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/services/online-consultation')}>Doctors</button>
          <span>›</span>
          <span className="bc-current">Book</span>
        </div>

        <div className="fade-up pharmacy-head">
          <h1 className="t-h1">Book your appointment</h1>
          <p className="t-body-sm">
            {isAutoAssign
              ? 'Fill in your details and a doctor will be auto-assigned to you.'
              : mode === 'online'
                ? 'Choose a time to connect with the doctor by video.'
                : 'Choose a time to visit the clinic.'}
          </p>
        </div>

        <div className="booking-grid">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Hospital info banner — for hospital flow */}
            {isHospitalFlow && hospital && (
              <div className="card" style={{ background: '#eef2ff', border: '1.5px solid #c7d2fe' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 32 }}>🏥</span>
                  <div style={{ flex: 1 }}>
                    <div className="t-h4" style={{ fontSize: 15, marginBottom: 2 }}>{hospital.name}</div>
                    <div className="t-body-sm" style={{ color: '#4338ca' }}>
                      {hospital.address || 'Walk-in consultation'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Doctor selection dropdown — for hospital flow */}
            {isHospitalFlow && (
              <div className="card">
                <h3 className="t-h3">Select Doctor</h3>
                <select
                  className="input-lg"
                  value={selectedDoctorId}
                  onChange={e => setSelectedDoctorId(e.target.value)}
                >
                  <option value="">Choose a doctor…</option>
                  {hospitalDoctors.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name} - {d.departmentLabel} ({d.yearsExperience} yrs exp)
                    </option>
                  ))}
                </select>
                {selectedDoctorId && doctor && (
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    background: '#f9fafb',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb'
                  }}>
                    <div className="t-body-sm" style={{ marginBottom: 4 }}>
                      <strong>{doctor.qualifications}</strong>
                    </div>
                    <div className="t-meta">
                      Fee: ₹{doctor.fee} · {doctor.yearsExperience} years experience
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Doctor strip — walk-in only (direct doctor booking) */}
            {!isAutoAssign && !isHospitalFlow && doctor && (
              <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <img
                  src={doctor.photoUrl}
                  alt={doctor.name}
                  style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #f3f4f6', flexShrink: 0 }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="t-h4" style={{ fontSize: 16, marginBottom: 2 }}>{doctor.name}</div>
                  <div className="t-body-sm" style={{ marginBottom: 4 }}>{doctor.qualifications}</div>
                  <div className="t-meta">{doctor.departmentLabel} · {doctor.yearsExperience} yrs experience</div>
                </div>
                <span style={{
                  background: '#eef2ff', color: '#4f46e5',
                  padding: '5px 12px', borderRadius: 9999, fontSize: 12.5, fontWeight: 600, flexShrink: 0,
                }}>
                  🏥 In-person
                </span>
              </div>
            )}

            {/* Auto-assign info banner */}
            {isAutoAssign && (
              <div className="card" style={{ background: '#ecfeff', border: '1.5px solid #a5f3fc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 32 }}>📱</span>
                  <div>
                    <div className="t-h4" style={{ fontSize: 15, marginBottom: 2 }}>Online Consultation</div>
                    <div className="t-body-sm" style={{ color: '#0e7490' }}>
                      {selectedDept
                        ? <>A <strong>{selectedDept}</strong> specialist will be auto-assigned based on earliest availability.</>
                        : 'Select a specialty below and a doctor will be auto-assigned.'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Department dropdown — shown for online auto-assign only (not hospital flow) */}
            {(isAutoAssign || (mode === 'walk-in' && !isHospitalFlow)) && (
              <div className="card">
                <h3 className="t-h3">Select Department</h3>
                <select
                  className="input-lg"
                  value={selectedDept}
                  onChange={e => setSelectedDept(e.target.value)}
                >
                  <option value="">Choose a specialty…</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.icon} {d.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Date picker */}
            <div className="card">
              <h3 className="t-h3">Pick a date</h3>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {dateOptions.map((d) => {
                  const active = d.iso === selectedDate;
                  return (
                    <button
                      key={d.iso}
                      onClick={() => setSelectedDate(d.iso)}
                      style={{
                        flexShrink: 0, minWidth: 76, padding: '10px 12px', borderRadius: 12,
                        border: `1.5px solid ${active ? '#4f46e5' : '#e0e7ff'}`,
                        background: active ? '#4f46e5' : '#fff',
                        color: active ? '#fff' : '#1e1b4b',
                        cursor: 'pointer', textAlign: 'center',
                        fontFamily: 'DM Sans, sans-serif', transition: 'all .15s',
                      }}
                    >
                      <div style={{ fontSize: 11.5, fontWeight: 500, opacity: active ? 0.85 : 0.7 }}>{d.label}</div>
                      <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>{d.day}</div>
                      <div style={{ fontSize: 11, opacity: active ? 0.85 : 0.6 }}>{d.month}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Slot picker — walk-in only */}
            {!isAutoAssign && (
              <div className="card">
                <h3 className="t-h3">Available time slots</h3>
                {isHospitalFlow && !selectedDoctorId ? (
                  <div className="t-body-sm" style={{ color: '#6b7280' }}>
                    Please select a doctor to view available slots.
                  </div>
                ) : slotsLoading ? (
                  <div className="t-body-sm">Loading slots…</div>
                ) : slots.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
                    {slots.map((s) => {
                      const active = selectedSlot?.id === s.id;
                      return (
                        <button
                          key={s.id}
                          disabled={s.booked}
                          onClick={() => setSelectedSlot(s)}
                          style={{
                            padding: '10px 6px', borderRadius: 10,
                            border: `1.5px solid ${active ? '#4f46e5' : s.booked ? '#f3f4f6' : '#e0e7ff'}`,
                            background: active ? '#4f46e5' : s.booked ? '#f9fafb' : '#fff',
                            color: active ? '#fff' : s.booked ? '#9ca3af' : '#1e1b4b',
                            cursor: s.booked ? 'not-allowed' : 'pointer',
                            fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600,
                            textDecoration: s.booked ? 'line-through' : 'none',
                            transition: 'all .12s',
                          }}
                        >
                          {s.time}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {!slotsLoading && slots.length > 0 && slots.every((s) => s.booked) && (
                  <div className="t-body-sm" style={{ marginTop: 12 }}>
                    No slots free this day — please pick another date.
                  </div>
                )}
              </div>
            )}

            {/* Second opinion */}
{!(mode === 'online' && !preSecondOpinion) && (
<div className="card">
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isSecondOpinion}
                  onChange={(e) => { setIsSecondOpinion(e.target.checked); if (!e.target.checked) setReportFile(null); }}
                  style={{ width: 18, height: 18, cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
                />
                <div>
                  <span className="t-label" style={{ margin: 0, display: 'block' }}>
                    🔍 This is a second opinion consultation
                  </span>
                  {isSecondOpinion && (
                    <div className="t-meta" style={{ marginTop: 6, color: '#6d28d9' }}>
                      Upload your existing diagnostic reports, scans, or prescriptions for the doctor's review (optional).
                    </div>
                  )}
                </div>
                
              </label>
              {isSecondOpinion && (
                <div style={{ marginTop: 16 }}>
                  <label className="t-label" style={{ display: 'block', marginBottom: 8 }}>
                    Upload previous medical reports (optional)
                  </label>
                  <label className={`file-dropzone${reportFile ? ' drag-over' : ''}`} style={{ display: 'block' }}>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      style={{ display: 'none' }}
                      onChange={(e) => setReportFile(e.target.files[0] || null)}
                    />
                    {reportFile ? (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 22 }}>📎</span>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13, color: '#4f46e5' }}>{reportFile.name}</div>
                          <div className="t-meta">{(reportFile.size / 1024).toFixed(1)} KB</div>
                        </div>
                        <button
                          onClick={(e) => { e.preventDefault(); setReportFile(null); }}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 18, padding: '0 4px' }}
                        >×</button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>📤</div>
                        <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13.5, color: '#4f46e5', marginBottom: 4 }}>
                          Click to upload or drag & drop
                        </div>
                        <div className="t-meta">PDF, JPG, PNG, DOC up to 10 MB</div>
                      </>
                    )}
                  </label>
                </div>
              )}
            </div>
)}
            {/* Patient details */}
            <div className="card">
              <h3 className="t-h3">Patient details</h3>
              <PatientAutofill onSelect={handleAutofill} label="Autofill from saved profile" />
              <div className="grid-2col">
                <div className="field">
                  <label className="t-label">Full name <span className="required-star">*</span></label>
                  <input className="input-lg" value={patient.name}
                    onChange={(e) => setPatient({ ...patient, name: e.target.value })}
                    placeholder="Patient's full name" />
                </div>
                <div className="field">
                  <label className="t-label">Mobile number <span className="required-star">*</span></label>
                  <input className="input-lg" type="tel" inputMode="numeric" maxLength={10}
                    value={patient.phone}
                    onChange={(e) => setPatient({ ...patient, phone: e.target.value.replace(/\D/g, '') })}
                    placeholder="10-digit number" />
                </div>
              </div>
              <div className="grid-3col">
                <div className="field">
                  <label className="t-label">Age <span className="required-star">*</span></label>
                  <input className="input-lg" type="number" min="0" max="120"
                    value={patient.age}
                    onChange={(e) => setPatient({ ...patient, age: e.target.value })}
                    placeholder="e.g. 32" />
                </div>
                <div className="field">
                  <label className="t-label">Gender <span className="required-star">*</span></label>
                  <select className="input-lg" value={patient.gender}
                    onChange={(e) => setPatient({ ...patient, gender: e.target.value })}>
                    <option value="">Select…</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                  </select>
                </div>
                <div className="field">
                  <label className="t-label">Email (optional)</label>
                  <input className="input-lg" type="email" value={patient.email}
                    onChange={(e) => setPatient({ ...patient, email: e.target.value })}
                    placeholder="you@example.com" />
                </div>
              </div>
              <div className="field">
                <label className="t-label">Relationship to Patient</label>
                <select className="input-lg" value={patient.relation}
                  onChange={(e) => setPatient({ ...patient, relation: e.target.value })}>
                  <option value="SELF">Self</option>
                  <option value="SPOUSE">Spouse</option>
                  <option value="CHILD">Child</option>
                  <option value="PARENT">Parent</option>
                  <option value="SIBLING">Sibling</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="field">
                <label className="t-label">Current Complaint</label>
                <textarea className="input-lg" rows={3} value={patient.currentComplaint}
                  onChange={(e) => setPatient({ ...patient, currentComplaint: e.target.value })}
                  placeholder="Describe your current symptoms or concern…"
                  style={{ resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div className="field">
                <label className="t-label">Previous Health History <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                <textarea className="input-lg" rows={3} value={patient.previousHealthHistory}
                  onChange={(e) => setPatient({ ...patient, previousHealthHistory: e.target.value })}
                  placeholder="Any past diagnoses, surgeries, allergies, or ongoing medications…"
                  style={{ resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <aside>
            <div className="card-sticky">
              <h3 className="t-h3">Booking summary</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span className="t-body-sm" style={{ flexShrink: 0 }}>Mode</span>
                  <span className="t-body-sm" style={{ color: '#1e1b4b', fontWeight: 500, textAlign: 'right' }}>
                    {isSecondOpinion ? '🔍 Second Opinion' : isAutoAssign ? '📱 Online Consultation' : '🏥 Doctor Visit'}
                  </span>
                </div>
                {isAutoAssign && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span className="t-body-sm" style={{ flexShrink: 0 }}>Specialty</span>
                    <span className="t-body-sm" style={{ color: '#1e1b4b', fontWeight: 500, textAlign: 'right', textTransform: 'capitalize' }}>
                      {department.replace(/-/g, ' ')}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span className="t-body-sm" style={{ flexShrink: 0 }}>Date</span>
                  <span className="t-body-sm" style={{ color: '#1e1b4b', fontWeight: 500, textAlign: 'right' }}>
                    {dateOptions.find((d) => d.iso === selectedDate)?.label} · {dateOptions.find((d) => d.iso === selectedDate)?.day} {dateOptions.find((d) => d.iso === selectedDate)?.month}
                  </span>
                </div>
                {!isAutoAssign && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span className="t-body-sm" style={{ flexShrink: 0 }}>Time</span>
                    <span className="t-body-sm" style={{ color: selectedSlot ? '#1e1b4b' : '#9ca3af', fontWeight: 500, textAlign: 'right' }}>
                      {selectedSlot?.time || 'Not selected'}
                    </span>
                  </div>
                )}
              </div>

              <div className="divider" />

              {!isAutoAssign && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span className="t-body-sm" style={{ flexShrink: 0 }}>Consultation fee</span>
                      <span className="t-body-sm" style={{ color: '#1e1b4b', fontWeight: 500 }}>₹{fee}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span className="t-body-sm" style={{ flexShrink: 0 }}>Platform fee</span>
                      <span className="t-body-sm" style={{ color: '#1e1b4b', fontWeight: 500 }}>₹{PLATFORM_FEE}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span className="t-body-sm" style={{ flexShrink: 0 }}>GST (18%)</span>
                      <span className="t-body-sm" style={{ color: '#1e1b4b', fontWeight: 500 }}>₹{tax}</span>
                    </div>
                  </div>
                  <div className="divider" />
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 16 }}>
                    <span className="t-h4" style={{ fontSize: 14, flexShrink: 0 }}>Total</span>
                    <span className="t-price">₹{total}</span>
                  </div>
                </>
              )}

              {isAutoAssign && (
                <div style={{ marginBottom: 16 }}>
                  <div className="t-meta" style={{ color: '#0e7490' }}>
                    Fee will be confirmed after doctor assignment.
                  </div>
                </div>
              )}

              {formError && (
                <div className="error-state" style={{ marginBottom: 12, fontSize: 13 }}>
                  <span>⚠️</span> {formError}
                </div>
              )}

              <button
                className="btn-primary"
                disabled={submitting || !isFormReady}
                onClick={handleSubmit}
                style={{
                  background: (!isFormReady || submitting) ? '#a5b4fc' : undefined,
                  cursor:     (!isFormReady || submitting) ? 'not-allowed' : 'pointer',
                  boxShadow:  (!isFormReady || submitting) ? 'none' : undefined,
                }}
              >
                {submitting ? 'Confirming…' : isAutoAssign ? 'Confirm Appointment' : `Confirm & Pay ₹${total}`}
              </button>

              <p className="t-meta" style={{ marginTop: 10, textAlign: 'center' }}>
                You'll receive a confirmation on +91 {patient.phone || '••••••••••'}
              </p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default BookAppointment;